import React, { useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { LogoMark, Wordmark } from '../ui/Logo';
import { useAuth } from '../../lib/auth';

interface AuthGateProps {
  children: React.ReactNode;
}

/**
 * Three possible states:
 *   1. Loading (check session)           → spinner
 *   2. Not signed in                     → login form
 *   3. Signed in but NOT the allowed id  → blocked screen + sign-out
 *   4. Signed in + allowed + is_admin()  → render children
 */
export const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const { loading, session, user, allowed, signIn, signOut } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const userEmail = user?.email ?? null;

  // ── STATE 1: loading ──
  if (loading) {
    return (
      <div className="min-h-dvh w-full flex items-center justify-center bg-white">
        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
      </div>
    );
  }

  // ── STATE 3: signed in as wrong user ──
  if (session && !allowed) {
    return (
      <div className="min-h-dvh w-full flex items-center justify-center bg-white p-6">
        <div className="w-full max-w-sm bg-white rounded-lg border border-stone-200 p-6 text-center">
          <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-rose-600" />
          </div>
          <h1 className="text-lg font-bold text-stone-900 mb-1">Access denied</h1>
          <p className="text-[13px] text-stone-500 mb-4 leading-relaxed">
            This site is restricted to a single account.
            {userEmail && <><br /><span className="text-stone-400">Signed in as {userEmail}</span></>}
          </p>
          <button
            onClick={signOut}
            className="w-full h-10 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // ── STATE 2: not signed in → login form ──
  if (!session) {
    const onSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setErr(null);
      const res = await signIn(email, password);
      setSubmitting(false);
      if (res.error) setErr(res.error);
    };

    return (
      <div className="min-h-dvh w-full flex items-center justify-center bg-white p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm bg-white rounded-lg border border-stone-200 p-6">
          {/* Brand */}
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <LogoMark className="w-7 h-7" />
            <Wordmark />
          </div>
          <p className="text-center mb-6 text-[12.5px] text-stone-500">Cockpit</p>

          <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Email</label>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-stone-200 bg-white focus:bg-white focus:border-indigo-400 focus:outline-none text-sm text-stone-900 mb-3 transition-colors"
          />

          <label className="block text-[11px] font-semibold text-stone-500 mb-1.5">Password</label>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-stone-200 bg-white focus:bg-white focus:border-indigo-400 focus:outline-none text-sm text-stone-900 mb-4 transition-colors"
          />

          {err && (
            <div className="mb-3 p-2.5 rounded-lg bg-rose-50 border border-rose-200">
              <p className="text-[12px] text-rose-700">{err}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !email || !password}
            className="w-full h-10 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors flex items-center justify-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  // ── STATE 4: authed + allowed → render app ──
  return <>{children}</>;
};
